import { createClient, filter } from '@prismicio/client';
import fetch from 'cross-fetch';

import config from '../../config';

import type { DocumentType } from './index.type';
import type { PrismicDocument } from '@prismicio/client';

const MAX_PAGE_SIZE = 100;
export const PREVIEW_LINK_RESOLVER = '/preview';
export const PREVIEW_TOOLBAR_SRC =
  'https://static.cdn.prismic.io/prismic.js?new=true&repo=thefarmersdog';
export const PREVIEW_TOOLBAR_SCRIPT = `<script async defer src="${PREVIEW_TOOLBAR_SRC}"></script>`;

const client = createClient(config.get('prismic.url'), {
  accessToken: config.get('prismic.token'),
  ref: config.get('prismic.ref'),
  // https://github.com/prismicio/prismic-client/issues/254
  fetch,
});

type QueryOptions = Omit<Parameters<(typeof client)['get']>[0], 'filters'>;

async function makePrismicRequest(
  query: string,
  options?: QueryOptions
): Promise<PrismicDocument[]> {
  const results: PrismicDocument[] = [];

  const response = await client.get({ filters: [query], ...options });
  if (!response.results) {
    throw new Error('Something went wrong, results not present');
  }

  results.push(...response.results);

  if (response.total_pages > 1) {
    for (let i = 1; i < response.total_pages; i += 1) {
      const page = await client.get({
        filters: [query],
        ...options,
        page: i + 1,
      });

      if (!page.results) {
        throw new Error('Something went wrong, results not present');
      }

      results.push(...page.results);
    }
  }

  return results;
}

export async function getAllDocuments(): Promise<PrismicDocument[]> {
  return makePrismicRequest('', { pageSize: MAX_PAGE_SIZE });
}

export async function getDocumentById(id: string): Promise<PrismicDocument> {
  const query = filter.at('document.id', id);
  const result = await makePrismicRequest(query);
  return result[0];
}

export async function getDocumentsByType(
  type: DocumentType[]
): Promise<PrismicDocument[]> {
  const query = filter.any('document.type', type);
  return await makePrismicRequest(query, { pageSize: MAX_PAGE_SIZE });
}
